import {useEffect, useRef} from 'react';
import {Client} from '@stomp/stompjs';
import SockJS from 'sockjs-client';

const WS_URL = process.env.REACT_APP_WS_URL || 'https://awp.minsky.ai/ws';

let sharedClient = null;
let pendingSubscriptions = [];
let connectPromise = null;

function getClient() {
    if (sharedClient && sharedClient.active) return Promise.resolve(sharedClient);
    if (connectPromise) return connectPromise;

    connectPromise = new Promise((resolve) => {
        const client = new Client({
            webSocketFactory: () => new SockJS(WS_URL),
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            onConnect: () => {
                sharedClient = client;
                connectPromise = null;
                // Flush pending subscriptions
                pendingSubscriptions.forEach(({topic, callback}) => {
                    client.subscribe(topic, callback);
                });
                pendingSubscriptions = [];
                resolve(client);
            },
            onStompError: () => {
                connectPromise = null;
            },
            onWebSocketError: () => {
                connectPromise = null;
            },
        });
        client.activate();
    });

    return connectPromise;
}

/**
 * Hook to subscribe to a STOMP topic. Calls onMessage whenever a message arrives.
 * Automatically unsubscribes on unmount or when topic changes.
 */
export function useStompSubscription(topic, onMessage) {
    const subRef = useRef(null);
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    useEffect(() => {
        if (!topic) return;

        let cancelled = false;

        getClient().then((client) => {
            if (cancelled) return;
            const sub = client.subscribe(topic, (frame) => {
                try {
                    const data = JSON.parse(frame.body);
                    onMessageRef.current(data);
                } catch (e) {
                    onMessageRef.current(frame.body);
                }
            });
            subRef.current = sub;
        }).catch((e) => {
            console.error('[useStompSubscription] Failed to subscribe to', topic, e);
        });

        return () => {
            cancelled = true;
            if (subRef.current) {
                try { subRef.current.unsubscribe(); } catch (_) {}
                subRef.current = null;
            }
        };
    }, [topic]);
}
